import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Radar, Target, Scan, Globe, ShieldAlert, Layers, Workflow, AlertTriangle, Crosshair, Shield, Zap, BookOpen } from 'lucide-react';
import TopBar from '../../components/TopBar';
import ContactFormModal from '../../components/ContactFormModal';
import PageTitle from '../../components/PageTitle';
import SiteFooter from '../../components/SiteFooter';

const capDefs = [
  { icon: Target, key: 1, wide: true },
  { icon: Scan, key: 2, wide: false },
  { icon: ShieldAlert, key: 3, wide: false },
  { icon: Layers, key: 4, wide: false },
  { icon: Globe, key: 5, wide: false },
  { icon: Workflow, key: 6, wide: true },
];

const ucDefs = [
  { icon: Crosshair, key: 1, accent: 'rose' as const },
  { icon: Shield, key: 2, accent: 'sky' as const },
  { icon: Zap, key: 3, accent: 'amber' as const },
  { icon: BookOpen, key: 4, accent: 'violet' as const },
];

const accentMap = {
  rose: { border: 'border-l-rose-500', bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400' },
  sky: { border: 'border-l-sky-500', bg: 'bg-sky-500/10', text: 'text-sky-600 dark:text-sky-400' },
  amber: { border: 'border-l-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  violet: { border: 'border-l-violet-500', bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
};

// --- Vulnerability Analysis Demo ---
const VulnDemo = () => {
  const { t } = useTranslation('products');
  const [activeTab, setActiveTab] = useState(0);
  const [progress, setProgress] = useState(0);

  // Reset progress when tab changes
  useEffect(() => {
    setProgress(0);
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 5) {
          clearInterval(timer);
          return 5;
        }
        return prev + 1;
      });
    }, 600);
    return () => clearInterval(timer);
  }, [activeTab]);

  // Auto-rotate tabs
  useEffect(() => {
    const rotation = setInterval(() => {
      setActiveTab(prev => (prev + 1) % 3);
    }, 7000);
    return () => clearInterval(rotation);
  }, []);

  const handleTabClick = useCallback((idx: number) => {
    setActiveTab(idx);
  }, []);

  const tabs = [
    t('vuln_mining.tab_disasm'),
    t('vuln_mining.tab_analysis'),
    t('vuln_mining.tab_poc'),
  ];

  return (
    <div className="relative w-full max-w-4xl mx-auto rounded-xl border border-border bg-card shadow-2xl overflow-hidden text-left">
      {/* Window chrome */}
      <div className="flex items-center px-4 py-3 border-b border-border bg-muted/50">
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500/80" />
          <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
          <div className="w-3 h-3 rounded-full bg-green-500/80" />
        </div>
        <div className="mx-auto text-xs text-muted-foreground font-mono">libserver.so — {t('vuln_mining.demo_uaf_analysis')}</div>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 py-2 border-b border-border bg-muted/20">
        {tabs.map((label, idx) => (
          <button
            key={idx}
            onClick={() => handleTabClick(idx)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${activeTab === idx
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
          >
            {label}
          </button>
        ))}
        <div className="ml-auto">
          {progress < 5 ? (
            <span className="flex items-center gap-1.5 text-[10px] text-amber-500 font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              {t('vuln_mining.scanning')}
            </span>
          ) : (
            <span className="text-[10px] bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded font-mono font-bold animate-fade-in">
              CWE-416
            </span>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="relative h-[480px] overflow-hidden">

        {/* ── Tab 0: Disassembly ── */}
        {activeTab === 0 && (
          <div className="p-4 md:p-6 pb-16 font-mono text-xs md:text-sm bg-background h-full relative animate-fade-in">
            <div className="space-y-0.5 text-foreground/80">
              {/* Prologue */}
              <div className="flex"><span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011a0</span><span className="text-blue-500 dark:text-blue-400 w-10">push</span> rbp</div>
              <div className="flex"><span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011a1</span><span className="text-blue-500 dark:text-blue-400 w-10">mov</span> rbp, rsp</div>
              <div className="flex"><span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011a4</span><span className="text-blue-500 dark:text-blue-400 w-10">sub</span> rsp, <span className="text-orange-500">0x20</span></div>
              {/* malloc */}
              <div className={`flex transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011a8</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">mov</span> edi, <span className="text-orange-500">0x48</span>
              </div>
              <div className={`flex transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011ad</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">call</span> <span className="text-yellow-600 dark:text-yellow-400">malloc@plt</span>
              </div>
              {/* vtable setup */}
              <div className={`flex transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011b2</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">mov</span> [rbp-<span className="text-orange-500">0x8</span>], rax
              </div>
              <div className={`flex transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011b6</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">lea</span> rcx, [rip+<span className="text-orange-500">0x2e43</span>]
                <span className="ml-4 text-muted-foreground/30 text-[10px]">; vtable_conn</span>
              </div>
              <div className={`flex transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011bd</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">mov</span> [rax], rcx
              </div>
              {/* recv + branch */}
              <div className={`flex transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011c0</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">call</span> <span className="text-yellow-600 dark:text-yellow-400">recv_data</span>
              </div>
              <div className={`flex transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011c5</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">test</span> eax, eax
              </div>
              <div className={`flex transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011c7</span>
                <span className="text-blue-500 dark:text-blue-400 w-10">jne</span> <span className="text-yellow-600 dark:text-yellow-400">0x4011e0</span>
              </div>
              {/* free — amber highlight */}
              <div className={`flex items-center rounded -mx-2 px-2 py-0.5 transition-all duration-300 ${progress >= 3 ? 'bg-amber-500/10 border-l-2 border-amber-500' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011c9</span>
                <span className={`w-10 ${progress >= 3 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-blue-500 dark:text-blue-400'}`}>call</span>
                <span className={progress >= 3 ? 'text-amber-600 dark:text-amber-300 font-semibold' : ''}>free@plt</span>
                {progress >= 3 && <span className="ml-auto text-[10px] text-amber-500 font-bold">← FREE</span>}
              </div>
              {/* dangling use — red highlight */}
              <div className={`flex items-center rounded -mx-2 px-2 py-0.5 transition-all duration-300 ${progress >= 4 ? 'bg-red-500/10 border-l-2 border-red-500' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011d4</span>
                <span className={`w-10 ${progress >= 4 ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-blue-500 dark:text-blue-400'}`}>mov</span>
                <span className={progress >= 4 ? 'text-red-600 dark:text-red-300 font-semibold' : ''}>rax, [rbp-<span className="text-orange-500">0x8</span>]</span>
                {progress >= 4 && <span className="ml-auto text-[10px] text-red-500 font-bold">← USE</span>}
              </div>
              <div className={`flex items-center rounded -mx-2 px-2 py-0.5 transition-all duration-300 ${progress >= 4 ? 'bg-red-500/10 border-l-2 border-red-500' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011d8</span>
                <span className={`w-10 ${progress >= 4 ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-blue-500 dark:text-blue-400'}`}>mov</span>
                <span className={progress >= 4 ? 'text-red-600 dark:text-red-300 font-semibold' : ''}>rax, [rax]</span>
              </div>
              <div className={`flex items-center rounded -mx-2 px-2 py-0.5 transition-all duration-300 ${progress >= 4 ? 'bg-red-500/10 border-l-2 border-red-500' : 'opacity-40'}`}>
                <span className="w-14 text-muted-foreground/40 select-none text-[11px]">0x4011db</span>
                <span className={`w-10 ${progress >= 4 ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-blue-500 dark:text-blue-400'}`}>call</span>
                <span className={progress >= 4 ? 'text-red-600 dark:text-red-300 font-semibold' : ''}>[rax+<span className="text-orange-500">0x10</span>]</span>
                {progress >= 4 && <span className="ml-auto text-[10px] text-red-500 font-bold">← RCE</span>}
              </div>
            </div>

            {/* Scanning overlay */}
            <div
              className="absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent to-emerald-500/8 border-b border-emerald-500/20 transition-all duration-700 ease-in-out pointer-events-none"
              style={{ top: `${(Math.min(progress, 4) / 4) * 85}%`, opacity: progress < 5 ? 1 : 0 }}
            />

            {/* Bottom badge */}
            {progress >= 5 && (
              <div className="absolute bottom-4 left-4 right-4 animate-fade-in">
                <div className="flex items-center justify-between gap-2 bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-xs border border-red-500/20">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span className="font-semibold">{t('vuln_mining.demo_vuln_uaf')}</span>
                  </div>
                  <span className="text-[10px] text-red-500/70 font-mono">{t('vuln_mining.demo_vtable_hijack')}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab 1: AI Analysis (Pseudo-C) ── */}
        {activeTab === 1 && (
          <div className="p-4 md:p-6 pb-16 font-mono text-xs md:text-sm bg-muted/20 h-full relative animate-fade-in">
            <div className="space-y-0.5 text-foreground/80">
              <div className={`transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">1</span> <span className="text-purple-600 dark:text-purple-400">void</span> <span className="text-blue-600 dark:text-blue-300">process_connection</span>(<span className="text-purple-600 dark:text-purple-400">int</span> fd) {'{'}</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">2</span>   <span className="text-purple-600 dark:text-purple-400">conn_t</span> *conn = <span className="text-blue-600 dark:text-blue-300">malloc</span>(<span className="text-orange-600 dark:text-orange-400">0x48</span>);</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">3</span>   conn-&gt;vtable = &amp;vtable_conn;</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">4</span>   conn-&gt;fd = fd;</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">5</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">6</span>   <span className="text-purple-600 dark:text-purple-400">int</span> rc = <span className="text-blue-600 dark:text-blue-300">recv_data</span>(conn);</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">7</span>   <span className="text-pink-600 dark:text-pink-400">if</span> (rc == <span className="text-orange-600 dark:text-orange-400">0</span>) {'{'}</div>
              </div>
              {/* free line — amber */}
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className={`flex items-center rounded -mx-1 px-1 py-0.5 ${progress >= 3 ? 'bg-amber-500/10 border-l-2 border-amber-500' : ''}`}>
                  <span className="w-6 text-muted-foreground/40 select-none">8</span>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="text-amber-600 dark:text-amber-300 font-semibold">free</span>(conn);
                  <span className="ml-auto text-[10px] text-amber-500 font-bold">← FREE</span>
                </div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">9</span>     <span className="text-muted-foreground italic">{'// ⚠ missing return — falls through!'}</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">10</span>  {'}'}</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">11</span></div>
              </div>
              {/* UAF line — red */}
              <div className={`transition-opacity duration-300 ${progress >= 4 ? 'opacity-100' : 'opacity-0'}`}>
                <div className={`flex items-center rounded -mx-1 px-1 py-0.5 ${progress >= 4 ? 'bg-red-500/10 border-l-2 border-red-500' : ''}`}>
                  <span className="w-6 text-muted-foreground/40 select-none">12</span>
                  &nbsp;&nbsp;conn-&gt;vtable-&gt;<span className="text-red-600 dark:text-red-300 font-semibold">handle</span>(conn);
                  <span className="ml-auto text-[10px] text-red-500 font-bold">⚠ UAF</span>
                </div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 4 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">13</span>{'}'}</div>
              </div>
            </div>

            {/* Bottom badges */}
            {progress >= 5 && (
              <div className="absolute bottom-4 left-4 right-4 animate-fade-in">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded font-mono font-bold">CWE-416</span>
                  <span className="text-[10px] bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded font-mono">{t('vuln_mining.demo_severity')}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab 2: POC Generation (Python) ── */}
        {activeTab === 2 && (
          <div className="p-4 md:p-6 pb-16 font-mono text-xs md:text-sm bg-background h-full relative animate-fade-in">
            <div className="space-y-0.5 text-foreground/80">
              <div className={`transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">1</span> <span className="text-purple-600 dark:text-purple-400">import</span> socket, struct, time</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">2</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">3</span> TARGET = <span className="text-green-700 dark:text-green-400">"192.168.1.100"</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 1 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">4</span> PORT &nbsp; = <span className="text-orange-600 dark:text-orange-400">4444</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">5</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">6</span> <span className="text-muted-foreground italic"># Step 1: trigger error path → free(conn)</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">7</span> s = socket.socket(socket.AF_INET, socket.SOCK_STREAM)</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 2 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">8</span> s.connect((TARGET, PORT))</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">9</span> s.send(<span className="text-green-700 dark:text-green-400">b"\x00"</span>) <span className="text-muted-foreground italic"># empty → rc==0 → free</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">10</span></div>
              </div>
              {/* Heap spray — highlighted */}
              <div className={`transition-opacity duration-300 ${progress >= 3 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">11</span> <span className="text-muted-foreground italic"># Step 2: heap spray → reclaim freed chunk</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 4 ? 'opacity-100' : 'opacity-0'}`}>
                <div className={`flex items-start rounded -mx-1 px-1 py-0.5 ${progress >= 4 ? 'bg-emerald-500/10 border-l-2 border-emerald-500' : ''}`}>
                  <span className="w-6 text-muted-foreground/40 select-none">12</span>
                  <span> fake_vtable = struct.pack(<span className="text-green-700 dark:text-green-400">"&lt;QQQ"</span>,</span>
                </div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 4 ? 'opacity-100' : 'opacity-0'}`}>
                <div className={`flex rounded -mx-1 px-1 ${progress >= 4 ? 'bg-emerald-500/10 border-l-2 border-emerald-500' : ''}`}>
                  <span className="w-6 text-muted-foreground/40 select-none">13</span>
                  <span>&nbsp;&nbsp;&nbsp;&nbsp;<span className="text-orange-600 dark:text-orange-400">0x0</span>, <span className="text-orange-600 dark:text-orange-400">0x0</span>, SHELLCODE_ADDR)</span>
                </div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 4 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">14</span> payload = fake_vtable + <span className="text-green-700 dark:text-green-400">b"\x90"</span>*<span className="text-orange-600 dark:text-orange-400">64</span> + shellcode</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 4 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">15</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 5 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">16</span> <span className="text-pink-600 dark:text-pink-400">for</span> _ <span className="text-pink-600 dark:text-pink-400">in</span> <span className="text-blue-600 dark:text-blue-300">range</span>(<span className="text-orange-600 dark:text-orange-400">200</span>):</div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 5 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">17</span>   s.send(payload) <span className="text-muted-foreground italic"># spray 0x48-sized chunks</span></div>
              </div>
              <div className={`transition-opacity duration-300 ${progress >= 5 ? 'opacity-100' : 'opacity-0'}`}>
                <div className="flex"><span className="w-6 text-muted-foreground/40 select-none">18</span>   time.sleep(<span className="text-orange-600 dark:text-orange-400">0.01</span>)</div>
              </div>
            </div>

            {/* Bottom badge */}
            {progress >= 5 && (
              <div className="absolute bottom-4 left-4 right-4 animate-fade-in">
                <div className="flex items-center gap-2 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-2 rounded-lg text-xs border border-emerald-500/20">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span className="font-semibold">{t('vuln_mining.demo_poc_ready')}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function VulnMining() {
  const { t } = useTranslation('products');
  const [showContactForm, setShowContactForm] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <PageTitle 
        titleKey="vuln_mining.title" 
        namespace="products" 
        descriptionKey="vuln_mining.subtitle"
      />
      <TopBar />

      {/* Hero */}
      <section className="relative pt-24 pb-5 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/[0.03] via-transparent to-transparent dark:from-emerald-500/[0.06]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(16,185,129,0.07) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        {/* Floating decorative orbs */}
        <div className="absolute top-16 left-[12%] w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-4 right-[12%] w-56 h-56 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />

        {/* Decorative radar illustration - left */}
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-48 h-64 text-emerald-500/10 hidden lg:block" viewBox="0 0 192 256" fill="none">
          <circle cx="0" cy="128" r="120" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="0" cy="128" r="80" stroke="currentColor" strokeWidth="1" strokeDasharray="4 4" />
          <circle cx="0" cy="128" r="40" stroke="currentColor" strokeWidth="1" />
          <path d="M0 128 L100 68" stroke="currentColor" strokeWidth="2" className="animate-pulse" />
          <circle cx="70" cy="80" r="4" fill="currentColor" className="animate-ping" />
        </svg>

        {/* Decorative target illustration - right */}
        <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-48 h-64 text-cyan-500/10 hidden lg:block" viewBox="0 0 192 256" fill="none">
          <circle cx="192" cy="128" r="100" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 28 V228" stroke="currentColor" strokeWidth="1" />
          <path d="M92 128 H292" stroke="currentColor" strokeWidth="1" />
          <circle cx="192" cy="128" r="60" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="192" cy="128" r="20" fill="currentColor" opacity="0.2" />
        </svg>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Icon */}
          <div className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
            <Radar className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-4 whitespace-nowrap">
            <span className="bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-600 bg-clip-text text-transparent">
              {t('vuln_mining.name')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mb-8">{t('vuln_mining.subtitle')}</p>

          <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mx-auto mb-4">
            {t('vuln_mining.desc_p1')}
          </p>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
            {t('vuln_mining.desc_p2')}
          </p>

          <div className="flex justify-center flex-wrap gap-4">
            <button
              onClick={() => setShowContactForm(true)}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/30 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('vuln_mining.cta')}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Live Vulnerability Demo */}
      <section className="pt-5 pb-10 px-4 sm:px-6 lg:px-8">

        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('vuln_mining.demo_title')}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t('vuln_mining.demo_desc')}
          </p>
          <VulnDemo />
        </div>
      </section>

      {/* Core Capabilities — Bento Grid */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('vuln_mining.capabilities_title')}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t('vuln_mining.capabilities_desc')}
          </p>

          {/* Pipeline indicator */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-1 mb-6">
            {(['cap_step1', 'cap_step2', 'cap_step3', 'cap_step4'] as const).map((key, i) => (
              <React.Fragment key={key}>
                {i > 0 && <div className="hidden sm:block w-6 h-px bg-emerald-500/30" />}
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                  <span className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                  {t(`vuln_mining.${key}`)}
                </div>
              </React.Fragment>
            ))}
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {capDefs.map(({ icon: Icon, key, wide }) => (
              <div
                key={key}
                className={`rounded-xl border border-border bg-card overflow-hidden transition-all hover:border-emerald-500/30 ${wide ? 'md:col-span-2' : ''
                  } ${key === 1 ? 'bg-gradient-to-br from-emerald-500/[0.04] to-cyan-500/[0.04]' : ''}`}
              >
                <div className="p-6 flex items-start gap-4">
                  <div className={`shrink-0 rounded-lg flex items-center justify-center ${key === 1
                    ? 'h-11 w-11 bg-gradient-to-br from-emerald-500 to-cyan-500'
                    : key === 6
                      ? 'h-11 w-11 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20'
                      : 'h-10 w-10 bg-emerald-500/10'
                    }`}>
                    <Icon className={`w-5 h-5 ${key === 1 ? 'text-white' : 'text-emerald-500'}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className={`font-semibold text-foreground tracking-tight mb-1.5 ${key === 1 || key === 6 ? 'text-lg' : 'text-base'}`}>
                      {t(`vuln_mining.cap${key}_title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(`vuln_mining.cap${key}_desc`)}</p>
                    {key === 6 && (
                      <div className="flex items-center gap-3 mt-3 text-xs text-emerald-600 dark:text-emerald-400 font-mono">
                        <span className="bg-emerald-500/10 px-2 py-0.5 rounded">Discover</span>
                        <span className="text-muted-foreground/30">→</span>
                        <span className="bg-emerald-500/10 px-2 py-0.5 rounded">POC</span>
                        <span className="text-muted-foreground/30">→</span>
                        <span className="bg-emerald-500/10 px-2 py-0.5 rounded">EXP</span>
                        <span className="text-muted-foreground/30">→</span>
                        <span className="bg-emerald-500/10 px-2 py-0.5 rounded">Post-Exploit</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('vuln_mining.usecases_title')}</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-2xl mx-auto">
            {t('vuln_mining.usecases_desc')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {ucDefs.map(({ icon: Icon, key, accent }) => {
              const c = accentMap[accent];
              return (
                <div key={key} className={`rounded-xl border border-border ${c.border} border-l-[3px] bg-card p-5 transition-all hover:border-l-4`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`h-8 w-8 rounded-lg ${c.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${c.text}`} />
                    </div>
                    <h3 className="font-semibold text-foreground tracking-tight">{t(`vuln_mining.uc${key}_title`)}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{t(`vuln_mining.uc${key}_desc`)}</p>
                  <span className={`inline-block text-[11px] ${c.bg} ${c.text} px-2 py-0.5 rounded-full font-medium`}>
                    {t(`vuln_mining.uc${key}_tag`)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <SiteFooter />

      <ContactFormModal isOpen={showContactForm} onClose={() => setShowContactForm(false)} product="vuln_mining" />
    </div>
  );
}
